"use client";
import { ReactNode, startTransition, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { APP_TAB, APP_TAB_PATH, AppTab } from "@/components/types";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import {
  CommandPalette,
  useCommandPalette,
} from "@/components/command-palette";
import IconSidebar from "@/components/layout/IconSidebar";
import TopBar from "@/components/layout/TopBar";
import { GlobalPatientDetailsDrawer } from "@/features/patient/components/GlobalPatientDetailsDrawer";
import { usePatientStore } from "@/features/patient/store/usePatientStore";
import CardCollection from "./views/CardCollection";

export default function PSRApp({
  children = <CardCollection />,
}: {
  children?: ReactNode;
} = {}) {
  const { tenantId } = useTenantRouteContext();
  const router = useRouter();
  const pathname = usePathname();
  const activeTab = pathname?.endsWith(APP_TAB_PATH[APP_TAB.PATIENT_AR])
    ? APP_TAB.PATIENT_AR
    : APP_TAB.CARD_COLLECTION;
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const cmdPalette = useCommandPalette();
  const setSelectedPatient = usePatientStore((s) => s.setSelectedPatient);

  const handleTabChange = (tab: AppTab) => {
    if (tab === activeTab) return;

    startTransition(() => router.push(`/${tenantId}${APP_TAB_PATH[tab]}`));
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <CommandPalette
        isOpen={cmdPalette.isOpen}
        onClose={cmdPalette.close}
        onNavigateToTab={cmdPalette.close}
        onSelectPatient={(patient) =>
          setSelectedPatient({ id: patient.patientId, initialData: patient })
        }
      />
      <GlobalPatientDetailsDrawer />
      <IconSidebar
        activeTab={activeTab}
        expanded={sidebarExpanded}
        onTabChange={handleTabChange}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          activeTab={activeTab}
          onOpenSearch={cmdPalette.open}
          sidebarExpanded={sidebarExpanded}
          onToggleSidebar={() => setSidebarExpanded((v) => !v)}
        />
        {children}
      </div>
    </div>
  );
}
